import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { AddonDTO, SubscriptionDTO } from '../../Types/Billing';
import { ChargebeeS } from '../ChargebeeS';
import SubscriptionS from '../restapi/subscriptionService';
import { BookitupScope } from '../ProfileS';
import { useJwtClaims } from './AuthContext';

interface BookitupSubscriptionContextInterface {
  subscription?: SubscriptionDTO;
  isActive: boolean;
  resourceAddon: AddonDTO | null;
  teamAddonPresent: boolean;
  refresh: () => void;
  loading: boolean;
  permissionsLoaded: boolean;
  openPortal: () => void;
}

type BookitupScopeSelection = {
  isOwner: boolean;
  isScopeSelected: boolean;
  loading: boolean;
};

export const BookitupSubscriptionContext = createContext<BookitupSubscriptionContextInterface>(
  {} as BookitupSubscriptionContextInterface,
);

const normalizeAddonName = (name: string) => name.toLowerCase().replace(/[^a-z0-9]/g, '');

const RESOURCE_ADDON_NAMES = ['Resource-Planning', 'Ressourcen'];
const TEAM_ADDON_NAMES = ['Team'];

const findAddon = (addons: AddonDTO[] | undefined, name: string) => {
  if (!addons || addons.length === 0) return null;
  const normalizedName = normalizeAddonName(name);
  return addons.find((addon) => normalizeAddonName(addon.name).startsWith(normalizedName)) ?? null;
};

const findResourceAddon = (addons: AddonDTO[] | undefined) =>
  RESOURCE_ADDON_NAMES.map((addonName) => findAddon(addons, addonName)).find(Boolean) ?? null;

const hasTeamAddon = (addons: AddonDTO[] | undefined) =>
  TEAM_ADDON_NAMES.some((addonName) => findAddon(addons, addonName));

export const BookitupSubscriptionProvider = () => {
  const [subscription, setSubscription] = useState<SubscriptionDTO>();
  const [loading, setLoading] = useState(false);
  const [loadedProfileId, setLoadedProfileId] = useState<number>();
  const { profileId } = useJwtClaims();
  const activeProfileIdRef = useRef(profileId);
  activeProfileIdRef.current = profileId;
  const { addons, status } = subscription ?? {};
  const resourceAddon = findResourceAddon(addons);
  const teamAddonPresent = hasTeamAddon(addons);
  const isActive = status === 'ACTIVE';
  const permissionsLoaded = Boolean(profileId && loadedProfileId === profileId);

  const fetchSubscriptionDetails = useCallback(async () => {
    if (!profileId) return;

    const requestedProfileId = profileId;
    setLoading(true);
    setLoadedProfileId(undefined);

    try {
      const resp = await SubscriptionS.getSubscription();
      if (resp.ok && activeProfileIdRef.current === requestedProfileId) {
        const nextSubscription = await resp.json();
        if (activeProfileIdRef.current === requestedProfileId) {
          setSubscription(nextSubscription);
        }
      }
    } finally {
      if (activeProfileIdRef.current === requestedProfileId) {
        setLoadedProfileId(requestedProfileId);
        setLoading(false);
      }
    }
  }, [profileId]);

  useEffect(() => {
    setSubscription(undefined);
    setLoadedProfileId(undefined);

    if (profileId) {
      fetchSubscriptionDetails();
    }
  }, [fetchSubscriptionDetails, profileId]);

  const openPortal = () => ChargebeeS.openPortal(fetchSubscriptionDetails);

  const value = useMemo(
    () => ({
      loading,
      permissionsLoaded,
      resourceAddon,
      teamAddonPresent,
      isActive,
      refresh: fetchSubscriptionDetails,
      subscription,
      openPortal,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading, permissionsLoaded, subscription, resourceAddon, teamAddonPresent],
  );

  return (
    <BookitupSubscriptionContext.Provider value={value}>
      <Outlet />
    </BookitupSubscriptionContext.Provider>
  );
};

export const useBookitupSubscription = () => useContext(BookitupSubscriptionContext);

export const useAddon = (name: string) => {
  const { subscription } = useBookitupSubscription();
  const { addons } = subscription ?? { addons: [] };
  const addon = findAddon(addons, name) ?? undefined;
  return { isPresent: addon !== undefined, addon };
};

export const useBookitupScope = (scopeOfInterest: BookitupScope): BookitupScopeSelection => {
  const { role, scopes } = useJwtClaims();
  const { permissionsLoaded, teamAddonPresent } = useBookitupSubscription();

  return useMemo(() => {
    const isOwner = !teamAddonPresent || !role || role === 'OWNER';
    const isScopeSelected = isOwner || scopes?.includes(scopeOfInterest);

    return {
      isOwner,
      isScopeSelected,
      loading: !permissionsLoaded,
    };
  }, [permissionsLoaded, role, scopes, scopeOfInterest, teamAddonPresent]);
};
